import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { envUrlControlMetadata, salesforceApiVersionControlMetadata } from './salesforce-common-metadata';
import { EnvironmentInjector } from '@angular/core';
import { getConnectorV1Name, getConnectorV2Name } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

const connectorLabel = getConnectorV1Name(stringResources.Salesforce_DisplayText);

const usernameControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    }
};

const passwordControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'password',
    enableAKVParameterization: true,
    label: stringResources.ModelName_Password,
    child: {
        type: 'parameterizable',
        attribute: 'password',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, connectorLabel),
        stringResources.ModelName_Password
    ),
    validators: [{ type: 'required', errorMessage: '' }]
};

const dynamicContentControls: ControlMetadata[] = [
    envUrlControlMetadata,
    usernameControl,
    passwordControl,
    {
        type: 'dynamic-akv-switcher',
        bind: 'securityToken',
        enableAKVParameterization: true,
        label: stringResources.ModelName_SecurityToken,
        child: {
            type: 'parameterizable',
            attribute: 'securityToken',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_SecurityToken,
                required: false,
                isLabelVertical: true
            }
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, connectorLabel),
            stringResources.ModelName_SecurityToken
        ),
        validators: []
    },
    {
        ...salesforceApiVersionControlMetadata,
        warningMessageCalculator: (value: string) =>
            value === 'Default' ? stringResources.Api_Version_Not_SupportDefault : ''
    }
];

export const salesforceMetadata = async (injector: EnvironmentInjector) => {
    const result: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.salesforce.linkedServiceType,
        displayName: connectorLabel,
        disabled: __IS_FABRIC_DPE__,
        isDeprecated: !GlobalVariables.showLegacy,
        notShowInTypeSelection: !GlobalVariables.showLegacy,
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt2,
            stringResources.LinkedServiceLowerCase,
            getConnectorV2Name(stringResources.Salesforce_DisplayText)
        ),
        warningMessageHyperlink:
            'https://learn.microsoft.com/azure/data-factory/connector-salesforce?tabs=data-factory',
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-Salesforce'
        },
        isFirstClass: true,
        fetchTableNameFromItemPath: true,
        tableNameAlias: 'objectApiName',
        tablePreviewConvert2QueryQuotes: ['"', '"'],
        supportDatasetV2: true,
        copySinkTypes: [adfModelTypeMap.salesforce.copySinkType],
        copySourceTypes: [adfModelTypeMap.salesforce.copySourceType],
        category: ['ServicesApps'],
        onetimeQueryTemplate: 'select <<field1>>, <<field2>> from <<objectName>>',
        timedQueryTemplate: 'select <<field1>>, <<field2>> from <<objectName>> \nwhere ' +
            '<<field1>> >= {ts\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm:ss\' )}\'} \nAND ' +
            '<<field1>> < {ts\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm:ss\' )}\'}',
        deltaLoadQueryTemplate: `select * from <<tableName>>
        where <<columnName>> > @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('{ts''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-dd HH:mm:ss'), '''}'))}
        and <<columnName>> <= @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('{ts''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-dd HH:mm:ss'), '''}'))}`,
        supportFirstClassParameterExperience: true,
        controls: dynamicContentControls
    };
    return result;
};
